import { useState, useEffect } from "react";
import { Play, Clock } from "lucide-react";
import { apiService } from "../utils/api";
import { extractYouTubeVideoId, YouTubePlayer } from "../components/shared/VideoPlayer";
import { CategorySidebar, ChannelCard } from "../components/shared/CategorySidebar";

const LiveTVPage = () => {
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, chanRes] = await Promise.all([
          apiService.getCategories(),
          apiService.getChannels(selectedCategory)
        ]);
        setCategories(catRes.data);
        setChannels(chanRes.data);
        if (!selectedChannel && chanRes.data.length > 0) {
          setSelectedChannel(chanRes.data[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const getYouTubeVideoId = (channel) => {
    if (!channel) return null;
    const youtubeUrl = channel.stream_url || channel.youtube_url || channel.current_program?.youtube_url;
    return extractYouTubeVideoId(youtubeUrl);
  };

  const handlePlayChannel = () => setIsPlaying(true);
  const youtubeVideoId = getYouTubeVideoId(selectedChannel);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-44" data-testid="live-tv-page">
      <div className="flex h-[calc(100vh-4rem)]">
        <CategorySidebar categories={categories} selectedCategory={selectedCategory} onSelectCategory={setSelectedCategory} />
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedChannel && (
            <div className="relative bg-black aspect-video max-h-[60vh]">
              {isPlaying && youtubeVideoId ? (
                <YouTubePlayer videoId={youtubeVideoId} autoplay={true} title={selectedChannel.name} onClose={() => setIsPlaying(false)} />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  {selectedChannel.current_program?.thumbnail ? (
                    <img src={selectedChannel.current_program.thumbnail} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <img src={selectedChannel.logo} alt="" className="w-32 h-32" />
                  )}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <button onClick={handlePlayChannel} className="bg-red-600 hover:bg-red-700 rounded-full p-6 transition-colors" data-testid="play-channel-btn">
                      <Play size={48} fill="white" className="text-white ml-1" />
                    </button>
                  </div>
                </div>
              )}
              {!isPlaying && (
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                  <div className="flex items-center gap-3">
                    <img src={selectedChannel.logo} alt="" className="w-12 h-12 rounded" />
                    <div>
                      <h2 className="text-xl font-bold">{selectedChannel.name}</h2>
                      {selectedChannel.current_program && <p className="text-gray-300">{selectedChannel.current_program.title}</p>}
                      {youtubeVideoId && (
                        <span className="inline-flex items-center gap-1 text-red-500 text-sm mt-1">
                          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                          YouTube Stream Available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4">
            <h3 className="text-lg font-bold mb-4">
              {selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : 'All Channels'}
              <span className="text-gray-500 font-normal ml-2">({channels.length})</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {channels.map((channel) => (
                <ChannelCard key={channel.id} channel={channel} isSelected={selectedChannel?.id === channel.id} onSelect={(ch) => { setSelectedChannel(ch); setIsPlaying(false); }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTVPage;
